import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Shield, ChevronDown, Loader, Menu, X, Home, Plus, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import LanguageSwitcher from './LanguageSwitcher';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, userProfile, signOut, canAdd } = useAuth();
  const { t } = useLocale();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const desktopDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns and mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsideDesktop = desktopDropdownRef.current && !desktopDropdownRef.current.contains(target);
      const clickedOutsideMobileMenu = mobileMenuRef.current && !mobileMenuRef.current.contains(target);

      // Close user dropdown if click is outside desktop dropdown
      if (clickedOutsideDesktop) {
        setShowUserDropdown(false);
      }

      // Close mobile menu if click is outside
      if (clickedOutsideMobileMenu) {
        setShowMobileMenu(false);
      }
    };

    if (showUserDropdown || showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown, showMobileMenu]);

  // Close dropdown and mobile menu when user logs out (cleanup)
  useEffect(() => {
    if (!user) {
      setShowUserDropdown(false);
      setShowMobileMenu(false);
      setSigningOut(false);
      setSignOutError(null);
    }
  }, [user]);

  const handleSignOut = async () => {
    if (signingOut) return; // Prevent multiple clicks

    try {
      setSigningOut(true);
      setSignOutError(null);
      await signOut();
      setShowUserDropdown(false);
      setShowMobileMenu(false);
    } catch (error) {
      console.error('Error signing out:', error);
      setSignOutError(t('nav.signOutError'));
    } finally {
      setSigningOut(false);
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email.charAt(0).toUpperCase();
  };

  const toggleDropdown = () => {
    if (!signingOut) {
      setShowUserDropdown(!showUserDropdown);
    }
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b relative z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <h1 className="text-xl font-bold text-gray-900">MC53</h1>
              </Link>
            </div>
            
            <nav className="hidden md:flex items-center space-x-2">
              {/* Navigation Links for Admin/Admin Light */}
              {canAdd() && (
                <>
                  <Link
                    to="/add"
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-700">{t('nav.addCarpet')}</span>
                  </Link>
                  <Link
                    to="/sold"
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Package className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-700">{t('nav.soldCarpets')}</span>
                  </Link>
                </>
              )}
              
              {/* Language Switcher */}
              <LanguageSwitcher />
              
              {/* User Dropdown */}
              {user && userProfile && (
                <div className="relative" ref={desktopDropdownRef}>
                  <button
                    onClick={toggleDropdown}
                    disabled={signingOut}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">{getUserInitials()}</span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>

                  {/* Dropdown Menu */}
                  {showUserDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium">{getUserInitials()}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                              userProfile.role === 'admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {userProfile.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                              {userProfile.role}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={handleSignOut}
                        disabled={signingOut}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {signingOut ? (
                          <Loader className="w-4 h-4 mr-3 animate-spin" />
                        ) : (
                          <LogOut className="w-4 h-4 mr-3" />
                        )}
                        {signingOut ? t('nav.signingOut') : t('nav.signOut')}
                      </button>

                      {signOutError && (
                        <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                          {signOutError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Mobile hamburger menu */}
            <div className="md:hidden" ref={mobileMenuRef}>
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                aria-label="Toggle mobile menu"
              >
                {showMobileMenu ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" />
                )}
              </button>

                             {/* Mobile Menu */}
               {showMobileMenu && (
                 <div className="absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50">
                   <div className="px-4 py-2">
                     {/* User Profile Section - At the top */}
                     {user && userProfile && (
                       <div className="px-3 py-3 border-b border-gray-100 mb-2">
                         <div className="flex items-center space-x-3">
                           <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                             <span className="text-white font-medium">{getUserInitials()}</span>
                           </div>
                           <div className="flex-1 min-w-0">
                             <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                             <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                               userProfile.role === 'admin' 
                                 ? 'bg-purple-100 text-purple-700' 
                                 : 'bg-green-100 text-green-700'
                             }`}>
                               {userProfile.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                               {userProfile.role}
                             </div>
                           </div>
                         </div>
                       </div>
                     )}

                     {/* Language Switcher */}
                     <div className="mb-2">
                       <LanguageSwitcher isMobile={true} />
                     </div>
                     
                     {/* Navigation Links */}
                     <div className="space-y-1">
                       <Link
                         to="/"
                         onClick={closeMobileMenu}
                         className="w-full flex items-center px-4 py-3 text-gray-700 active:bg-gray-100 rounded-lg transition-colors font-medium"
                       >
                         <Home className="w-5 h-5 mr-3" />
                         {t('nav.home')}
                       </Link>

                      {/* Add Carpet Link - for admins and admin_light */}
                      {canAdd() && (
                        <>
                          <Link
                            to="/add"
                            onClick={closeMobileMenu}
                            className="w-full flex items-center px-4 py-3 text-gray-700 active:bg-gray-100 rounded-lg transition-colors font-medium"
                          >
                            <Plus className="w-5 h-5 mr-3" />
                            {t('nav.addCarpet')}
                          </Link>
                          <Link
                            to="/sold"
                            onClick={closeMobileMenu}
                            className="w-full flex items-center px-4 py-3 text-gray-700 active:bg-gray-100 rounded-lg transition-colors font-medium"
                          >
                            <Package className="w-5 h-5 mr-3" />
                            {t('nav.soldCarpets')}
                          </Link>
                        </>
                      )}
                     </div>

                     {/* Sign Out - At the bottom */}
                     {user && userProfile && (
                       <div className="mt-1">
                         <button
                           onClick={handleSignOut}
                           disabled={signingOut}
                           className="w-full flex items-center px-4 py-3 text-gray-700 active:bg-gray-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                         >
                           {signingOut ? (
                             <Loader className="w-5 h-5 mr-3 animate-spin" />
                           ) : (
                             <LogOut className="w-5 h-5 mr-3" />
                           )}
                           {signingOut ? t('nav.signingOut') : t('nav.signOut')}
                         </button>

                         {signOutError && (
                           <div className="mx-4 mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                             {signOutError}
                           </div>
                         )}
                       </div>
                     )}
                   </div>
                 </div>
               )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        {children}
      </main>
    </div>
  );
};

export default Layout; 